import { useCallback, useEffect, useMemo, useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { CapabilityNotice } from "@/components/bse/BseUatForm";
import { useAdminBseCapability } from "@/components/BseCapabilityStatus";
import { adminApi, getApiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatDateTime } from "@/lib/format";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Eye, RefreshCw, Send, ShieldAlert, XCircle } from "lucide-react";
import { MODE_META, buildSpec, validateSpec } from "@/components/bse/UccUpdateForms";

const MODE_OPTIONS = Object.entries(MODE_META).map(([value, meta]) => ({ value, label: meta.label }));

function humanizeOp(op) {
  if (!op) return "-";
  if (op === "update_ucc:status_inactive") return "Deactivate UCC";
  if (op.startsWith("update_ucc:")) {
    const field = op.slice("update_ucc:".length);
    return `Update — ${MODE_META[field]?.label || field}`;
  }
  if (op.startsWith("add_ucc:")) return `Register — ${op.slice("add_ucc:".length)}`;
  return op;
}

function ResultPanel({ result }) {
  if (!result) return null;
  const status = result.status || "UNKNOWN";
  const success = status === "SUBMITTED";
  const pending = ["SUBMITTING", "UNKNOWN", "WAITING_2FA"].includes(status);
  const Icon = success ? CheckCircle2 : pending ? AlertTriangle : XCircle;
  const tone = success ? "border-emerald-200 bg-emerald-50 text-emerald-900" : pending ? "border-amber-200 bg-amber-50 text-amber-900" : "border-rose-200 bg-rose-50 text-rose-900";
  return (
    <div className={`rounded-xl border p-4 text-sm ${tone}`} data-testid="ucc-update-result">
      <div className="flex flex-wrap items-center gap-2">
        <Icon className="h-4 w-4" />
        <span className="font-semibold">{status.replaceAll("_", " ")}</span>
        {result.field && <Badge variant="outline" className="bg-white">{result.field}</Badge>}
        {result.env && <Badge variant="outline" className="bg-white">{result.env}</Badge>}
      </div>
      {/* BSE rejections come back as specific field-level text here -- never collapse this into a generic toast. */}
      <p className="mt-2 leading-relaxed">{result.message || "No message returned."}</p>
    </div>
  );
}

export default function UccServicing() {
  const capability = useAdminBseCapability("ucc_registration");
  const [clientCode, setClientCode] = useState("");
  const [mode, setMode] = useState("bank");
  const [specs, setSpecs] = useState(() => Object.fromEntries(Object.entries(MODE_META).map(([key, meta]) => [key, meta.empty()])));
  const [preview, setPreview] = useState(null);
  const [previewedKey, setPreviewedKey] = useState(null);
  const [submitResult, setSubmitResult] = useState(null);
  const [busy, setBusy] = useState("");

  const [ops, setOps] = useState({ items: [], loading: true, error: "" });
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deactivateCode, setDeactivateCode] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [deactivateBusy, setDeactivateBusy] = useState(false);
  const [deactivateResult, setDeactivateResult] = useState(null);

  const spec = specs[mode];
  const { Form, label: modeLabel } = MODE_META[mode];
  const updateSpec = (next) => setSpecs((prev) => ({ ...prev, [mode]: next }));

  const normalizedCode = clientCode.trim().toUpperCase();
  const builtSpec = useMemo(() => buildSpec(mode, spec), [mode, spec]);
  const validationError = useMemo(() => validateSpec(mode, spec), [mode, spec]);
  const requestKey = useMemo(
    () => JSON.stringify({ client_code: normalizedCode, field: mode, spec: builtSpec }),
    [normalizedCode, mode, builtSpec],
  );
  const previewIsCurrent = previewedKey === requestKey;

  const loadOps = useCallback(async () => {
    setOps((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      const { data } = await adminApi.get("/admin/bse2/ucc/ops", { params: { limit: 50 }, showErrorToast: false });
      setOps({ items: data.ops || [], loading: false, error: "" });
    } catch (error) {
      setOps({ items: [], loading: false, error: getApiErrorMessage(error) });
    }
  }, []);
  useEffect(() => { loadOps(); }, [loadOps]);

  const changeMode = (nextMode) => {
    setMode(nextMode);
    setPreview(null);
    setPreviewedKey(null);
    setSubmitResult(null);
  };

  const runPreview = async () => {
    if (!normalizedCode) return toast.error("Enter a UCC / client code first.");
    if (validationError) return toast.error(validationError);
    setBusy("preview");
    setSubmitResult(null);
    try {
      const { data } = await adminApi.post("/admin/bse2/ucc/update", { client_code: normalizedCode, field: mode, spec: builtSpec, dry_run: true });
      setPreview(data.payload);
      setPreviewedKey(requestKey);
      toast.success("Preview built. Review the exact BSE payload before submitting.");
    } catch (error) {
      setPreview(null);
      setPreviewedKey(null);
      toast.error(getApiErrorMessage(error));
    } finally {
      setBusy("");
    }
  };

  const runSubmit = async () => {
    if (!previewIsCurrent) return toast.error("The form changed after preview. Preview the exact payload again before submitting.");
    if (!window.confirm(`Submit this ${modeLabel} update for UCC ${normalizedCode} to BSE?`)) return;
    setBusy("submit");
    try {
      const { data } = await adminApi.post("/admin/bse2/ucc/update", { client_code: normalizedCode, field: mode, spec: builtSpec, dry_run: false });
      setSubmitResult(data);
      if (data.status === "SUBMITTED") {
        toast.success(data.message || "Update submitted to BSE");
        setPreview(null);
        setPreviewedKey(null);
      } else {
        toast.error(data.message || `BSE returned ${data.status || "an error"}`);
      }
      loadOps();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setBusy("");
    }
  };

  const openDeactivate = () => {
    if (!normalizedCode) return toast.error("Enter a UCC / client code first.");
    setDeactivateCode(normalizedCode);
    setConfirmText("");
    setDeactivateResult(null);
    setDeactivateOpen(true);
  };
  const confirmMatches = confirmText.trim().toUpperCase() === deactivateCode && deactivateCode.length > 0;

  const runDeactivate = async () => {
    if (!confirmMatches) return;
    setDeactivateBusy(true);
    try {
      const { data } = await adminApi.post("/admin/bse2/ucc/deactivate", { client_code: deactivateCode });
      setDeactivateResult(data);
      if (data.status === "SUBMITTED") {
        toast.success(data.message || `UCC ${deactivateCode} set INACTIVE`);
        setDeactivateOpen(false);
      } else {
        toast.error(data.message || `BSE returned ${data.status || "an error"}`);
      }
      loadOps();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setDeactivateBusy(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div>
          <h1 className="font-heading text-3xl text-[var(--brand)]">UCC servicing</h1>
          <p className="mt-1 max-w-3xl text-sm text-[var(--ink-muted)]">
            Update a registered investor&apos;s bank, depository, address, contact, nominee, FATCA, holder,
            identifier or holding-nature details on BSE, or deactivate the UCC. Every update must be
            previewed as the exact wire payload before it can be submitted.
          </p>
        </div>
        <CapabilityNotice capability={capability} title="BSE UCC servicing" />

        <Card>
          <CardHeader><CardTitle className="text-lg">Investor and update mode</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-[var(--ink-muted)]">UCC / client code</Label>
                <Input className="h-10" value={clientCode} placeholder="e.g. 1234567890"
                  onChange={(e) => setClientCode(e.target.value)} data-testid="ucc-servicing-client-code" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-[var(--ink-muted)]">Update mode</Label>
                <select className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm" value={mode}
                  onChange={(e) => changeMode(e.target.value)} data-testid="ucc-servicing-mode">
                  {MODE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg">{modeLabel}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <Form value={spec} onChange={updateSpec} />
            {validationError && <p className="text-xs text-amber-700">{validationError}</p>}
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" disabled={busy !== "" || !!validationError}
                onClick={runPreview} data-testid="ucc-servicing-preview">
                <Eye className="mr-2 h-4 w-4" />{busy === "preview" ? "Building preview…" : "Preview exact wire payload"}
              </Button>
              <Button type="button" disabled={busy !== "" || !preview || !previewIsCurrent}
                onClick={runSubmit} data-testid="ucc-servicing-submit">
                <Send className="mr-2 h-4 w-4" />{busy === "submit" ? "Submitting…" : "Submit to BSE"}
              </Button>
            </div>
            {preview && (
              <div>
                <p className="mb-2 text-xs text-[var(--ink-muted)]">
                  This is the body that will be passed to <code>update_ucc</code>.
                  {!previewIsCurrent && " The form changed since this preview — build it again before submitting."}
                </p>
                <Textarea readOnly className="min-h-[320px] font-mono text-xs" value={JSON.stringify(preview, null, 2)} />
              </div>
            )}
            <ResultPanel result={submitResult} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-lg">Recent UCC operations</CardTitle>
            <Button variant="outline" size="sm" onClick={loadOps} disabled={ops.loading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${ops.loading ? "animate-spin" : ""}`} />Refresh
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {ops.loading ? (
              <div className="p-6 text-sm text-[var(--ink-muted)]">Loading recent operations…</div>
            ) : ops.error ? (
              <div className="p-6 text-sm text-rose-700">{ops.error}</div>
            ) : !ops.items.length ? (
              <div className="p-6 text-sm text-[var(--ink-muted)]">No UCC operations have been recorded yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Client code</TableHead>
                      <TableHead>Operation</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Message</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ops.items.map((item, index) => {
                      const result = item.result || {};
                      const status = result.status || "UNKNOWN";
                      const tone = status === "SUBMITTED" ? "text-emerald-700" : status === "FAILED" ? "text-rose-700" : "text-amber-700";
                      return (
                        <TableRow key={`${item.client_code}-${item.at || index}`}>
                          <TableCell className="whitespace-nowrap text-xs">{formatDateTime(item.at)}</TableCell>
                          <TableCell className="font-mono text-xs">{item.client_code || "—"}</TableCell>
                          <TableCell className="text-xs">{humanizeOp(item.op)}</TableCell>
                          <TableCell className={`text-xs font-semibold ${tone}`}>{status.replaceAll("_", " ")}</TableCell>
                          <TableCell className="max-w-[420px] text-xs text-[var(--ink-muted)]">{result.message || "—"}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-rose-200">
          <CardHeader><CardTitle className="text-lg text-rose-800">Deactivate UCC</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-[var(--ink-muted)]">
              Sets the UCC entered above to INACTIVE on BSE. This is irreversible from this screen — a
              deactivated UCC cannot be reactivated here.
            </p>
            <Button type="button" variant="destructive" disabled={!normalizedCode} onClick={openDeactivate} data-testid="ucc-servicing-deactivate-open">
              <ShieldAlert className="mr-2 h-4 w-4" />Deactivate UCC {normalizedCode || ""}
            </Button>
          </CardContent>
        </Card>
      </div>

      <Dialog open={deactivateOpen} onOpenChange={(open) => { if (!deactivateBusy) setDeactivateOpen(open); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deactivate UCC {deactivateCode}</DialogTitle>
            <DialogDescription>
              This sends update_ucc with status INACTIVE to BSE for {deactivateCode}. It cannot be undone from
              this screen. Type the client code exactly to confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-[var(--ink-muted)]">Type {deactivateCode} to confirm</Label>
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} autoFocus data-testid="ucc-servicing-deactivate-confirm-input" />
          </div>
          {deactivateResult && <ResultPanel result={deactivateResult} />}
          <DialogFooter className="gap-2">
            <Button variant="outline" disabled={deactivateBusy} onClick={() => setDeactivateOpen(false)}>Cancel</Button>
            <Button variant="destructive" disabled={!confirmMatches || deactivateBusy} onClick={runDeactivate} data-testid="ucc-servicing-deactivate-confirm">
              <ShieldAlert className="mr-2 h-4 w-4" />{deactivateBusy ? "Deactivating…" : "Yes, deactivate this UCC"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
